import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import { swalApiError, swalApiSuccess } from 'app/utility/Swalutility';
import UploadSnackbar from '../../main/pages/ticket-conversation/facebookPosts/UploadSnackbar';

const FacebookUploadsContext = createContext(null);

export const useFacebookUploads = () => {
	const ctx = useContext(FacebookUploadsContext);
	if (!ctx) {
		throw new Error('useFacebookUploads must be used inside a FacebookUploadsProvider');
	}
	return ctx;
};

const typeToLabel = type => {
	if (type === 'reel') return 'reel';
	if (type === 'video') return 'video';
	if (type === 'image') return 'photo';
	return 'post';
};

export const FacebookUploadsProvider = ({ children }) => {
	const [uploads, setUploads] = useState([]);
	const listenersRef = useRef(new Set());

	const dismiss = useCallback(id => {
		setUploads(prev => prev.filter(u => u.id !== id));
	}, []);

	const enqueue = useCallback(
		({ payload, postType, pageId, pageName, isEditing, isScheduled, editedId, clientId }) => {
			const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
			const typeLabel = typeToLabel(postType);
			const label = isEditing
				? `Updating ${typeLabel} on ${pageName}`
				: isScheduled
				? `Scheduling ${typeLabel} on ${pageName}`
				: `Publishing ${typeLabel} on ${pageName}`;

			setUploads(prev => [
				...prev,
				{ id, label, type: postType, status: 'uploading', pageId, pageName }
			]);

			const payloadWithClient = {
				...payload,
				clientId: clientId ? String(clientId) : undefined
			};

			CoreHttpHandler.request(
				'facebookPosts',
				'facebookPostsPublishPost',
				payloadWithClient,
				response => {
					const successLabel = isEditing
						? 'Post updated'
						: isScheduled
						? 'Post scheduled'
						: 'Post published';
					setUploads(prev =>
						prev.map(u => (u.id === id ? { ...u, status: 'success', successLabel } : u))
					);
					setTimeout(() => {
						setUploads(prev => prev.filter(u => u.id !== id));
					}, 3000);
					// Show the API's message in a Swal so the user sees the
					// backend's exact confirmation alongside the snackbar.
					swalApiSuccess(response, successLabel);
					listenersRef.current.forEach(fn => {
						try {
							fn({ status: 'success', pageId, editedId, isEditing });
						} catch (e) {
							/* swallow listener errors so one bad subscriber can't break others */
						}
					});
				},
				err => {
					const message = err?.response?.data?.message || 'Upload failed';
					setUploads(prev =>
						prev.map(u =>
							u.id === id ? { ...u, status: 'error', error: message } : u
						)
					);
					setTimeout(() => {
						setUploads(prev => prev.filter(u => u.id !== id));
					}, 6000);
					// Surface the actual API error (handles 500s with { message }
					// body via the shared extractor).
					swalApiError(err, 'Upload failed');
					listenersRef.current.forEach(fn => {
						try {
							fn({ status: 'error', pageId, editedId, isEditing, error: message });
						} catch (e) {
							/* swallow */
						}
					});
				}
			);
		},
		[]
	);

	const onComplete = useCallback(listener => {
		listenersRef.current.add(listener);
		return () => {
			listenersRef.current.delete(listener);
		};
	}, []);

	const value = { uploads, enqueue, dismiss, onComplete };

	return (
		<FacebookUploadsContext.Provider value={value}>
			{children}
			<UploadSnackbar uploads={uploads} onDismiss={dismiss} />
		</FacebookUploadsContext.Provider>
	);
};

export default FacebookUploadsContext;
